import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid } from '@mui/material';
import { Link, Route, Routes } from 'react-router-dom';
import { getCurrentUser, signOut, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { send_request } from '../../utils/Request';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import AppRoutes from '../../Routes';
import Sidebar from './Sidebar';
import W86Backdrop from './W86Backdrop';
import { CustomSignIn } from './CustomSignIn';
import aws_config from '../../auth/awsConfig';

function isAuthRevokedError(error) {
  const message = error?.message || '';
  const code = error?.code || error?.name || '';
  return (
    code === 'NotAuthorizedException' ||
    message === 'No current user' ||
    message.includes('Access Token has been revoked')
  );
}

function AppShell({ authState, updateUsername, onSignOut: parentSignOut, onStateChange }) {
  const [state, setState] = useState({
    roleAdministrator: false,
    roleEditor: false,
    rolePrivateViewer: false,
    tier: '',
    orgName: '',
    clientId: null,
    userDisplayName: '',
    firstLogin: false,
    initialized: false,
  });

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      const gapi = window.gapi;
      if (gapi) {
        await new Promise((resolve) => gapi.load('auth2', resolve));
        const auth2 = await gapi.auth2.init({ client_id: aws_config.gg_client_id });
        if (auth2.isSignedIn.get()) {
          await auth2.signOut();
        }
      }
      window.location.pathname = '/';
    } catch (err) {
      console.error('Error signing out:', err);
      window.location.pathname = '/';
    }
  }, []);

  const initializeBillingSystem = useCallback(async (clientId) => {
    try {
      const billingVersionResponse = await send_request(
        `authz-service/v2/billing/is-v2-billing/${clientId}`,
        '',
        {}
      );

      if (!billingVersionResponse?.data) {
        console.warn('Failed to retrieve billing version information');
        useProjectStore.setState({ isV2Billing: false, billingInitialized: true });
        return;
      }

      const { isV2Billing } = billingVersionResponse.data;
      useProjectStore.setState({ isV2Billing });

      if (!isV2Billing) {
        useProjectStore.setState({ billingInitialized: true });
        return;
      }

      const subscriptionResponse = await send_request(
        'authz-service/v2/billing/subscription',
        'POST',
        {}
      );

      if (!subscriptionResponse?.data?.plan) {
        console.warn('Failed to retrieve subscription plan information');
        useProjectStore.setState({ billingInitialized: true });
        return;
      }

      const currentPlan = subscriptionResponse.data.plan;
      useProjectStore.setState({ tier: currentPlan, billingInitialized: true });
      setState((prev) => ({ ...prev, tier: currentPlan }));
    } catch (error) {
      console.error('Error initializing billing system:', error);
      useProjectStore.setState({ isV2Billing: false, billingInitialized: true });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initialize() {
      useProjectStore.setState({
        isV2Billing: undefined,
        billingInitialized: false,
      });

      let email = null;
      try {
        const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};
        const currentUser = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        email = userAttributes.email || idToken.payload.email;
      } catch (error) {
        console.log('Error fetching auth session:', error);
        if (isAuthRevokedError(error)) {
          await handleSignOut();
          return;
        }
      }

      const [checkUserResponse, json] = await Promise.all([
        email
          ? send_request(
              'authz-service/check-user-verified-and-completed',
              { email },
              '',
              'POST'
            ).catch((error) => {
              console.log('An error occurred while checking user status:', error);
              return null;
            })
          : Promise.resolve(null),
        send_request('authz-service/user-info', '', {}),
      ]);

      if (cancelled) return;

      let firstLogin = false;
      if (checkUserResponse?.data) {
        firstLogin = checkUserResponse.data.verified === false;
      }

      if (json?.data) {
        const d = json.data;
        setState({
          roleAdministrator: d.roleAdministrator,
          roleEditor: d.roleEditor,
          rolePrivateViewer: d.rolePrivateViewer,
          tier: d.client.tier,
          orgName: d.client.clientName || '',
          clientId: d.client.clientId || null,
          userDisplayName: d.displayName || '',
          firstLogin,
          initialized: true,
        });

        useProjectStore.setState({
          timezone: d.timezone,
          tier: d.client.tier,
          orgName: d.client.clientName,
          clientId: d.client.clientId,
          userName: d.userName,
          userDisplayName: d.displayName || d.userName,
        });

        await initializeBillingSystem(d.client.clientId);

        fetchUserAttributes().catch(async (error) => {
          console.log('Error fetching user attributes:', error);
          if (isAuthRevokedError(error)) {
            await handleSignOut();
          }
        });

        if (useProjectStore.getState().timezone == null) {
          useProjectStore.setState({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
        }
      } else {
        useProjectStore.setState({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
        setState((prev) => ({ ...prev, firstLogin, initialized: true }));
      }
    }

    initialize();
    return () => { cancelled = true; };
  }, [handleSignOut, initializeBillingSystem]);

  if (!state.initialized) return null;

  if (state.firstLogin) {
    return (
      <Routes>
        <Route
          path="/"
          element={
            <CustomSignIn
              authState={authState}
              updateUsername={updateUsername}
              onStateChange={onStateChange}
              firstLogin={(val) => setState((prev) => ({ ...prev, firstLogin: val }))}
            />
          }
        />
      </Routes>
    );
  }

  if (authState !== 'signedIn') return null;

  return (
    <Grid
      container
      direction="column"
      style={{ height: '100vh', minWidth: 0, overflow: 'hidden' }}
    >
      <W86Backdrop />
      {state.tier === 'FREE' && (
        <Grid container className="freeTierTopBar" alignItems="center">
          <Grid item xs={11} className="freeTierTopBarText">
            You are currently on the free plan. Run more actions and unlock premium features by{' '}
            <Link style={{ fontWeight: 'bold', textDecoration: 'none' }} to="/set_plan">
              upgrading now
            </Link>
          </Grid>
          <Grid
            item
            xs={1}
            onClick={() => setState((prev) => ({ ...prev, tier: '' }))}
            sx={{ color: '#ffffff', cursor: 'pointer', textAlign: 'center' }}
          >
            x
          </Grid>
        </Grid>
      )}
      <Grid
        item
        style={{
          display: 'flex',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Sidebar
          roleAdministrator={state.roleAdministrator}
          roleEditor={state.roleEditor}
          rolePrivateViewer={state.rolePrivateViewer}
          clientId={state.clientId}
          tier={state.tier}
          userDisplayName={state.userDisplayName}
          orgName={state.orgName}
          onSignOut={handleSignOut}
        />
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AppRoutes firstLogin={state.firstLogin} />
        </Box>
      </Grid>
    </Grid>
  );
}

export default AppShell;
